// src/timetableUtils.js
// Расчёты по основному расписанию из МЭШ (/api/timetable/): что идёт сейчас,
// сколько осталось до конца урока или перемены. Та же логика есть в мобильном
// приложении (src/lib/timetable.ts) — правки вносить в оба места.

import { plural } from './dateUtils';

export const DAY_NAMES = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
export const DAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const MSK_OFFSET_MS = 3 * 60 * 60 * 1000; // Москва — UTC+3 круглый год

// Текущий момент по Москве, независимо от часового пояса компьютера
export function moscowNow(date = new Date()) {
  const msk = new Date(date.getTime() + MSK_OFFSET_MS);
  return {
    day: (msk.getUTCDay() + 6) % 7, // 0 — понедельник … 6 — воскресенье
    seconds: msk.getUTCHours() * 3600 + msk.getUTCMinutes() * 60 + msk.getUTCSeconds(),
  };
}

// Сегодняшняя дата по Москве в виде «2026-10-13» — для сравнения с «актуально до»
export function moscowDate(date = new Date()) {
  return new Date(date.getTime() + MSK_OFFSET_MS).toISOString().slice(0, 10);
}

// Видно ли основное расписание учителям прямо сейчас. Сервер тоже это проверяет,
// но данные в браузере перечитываются раз в 10 минут — дату сверяем и здесь,
// чтобы с наступлением даты расписание пропадало сразу.
export function isTimetableAvailable(timetable, date = new Date()) {
  if (!timetable || timetable.hidden) return false;
  if (timetable.valid_until && moscowDate(date) > timetable.valid_until) return false;
  return true;
}

// «13 октября 2026»
export function formatDay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
  return `${d} ${months[m - 1]} ${y}`;
}

export const toSeconds = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 3600 + m * 60;
};

// Порядок классов: 1А, 1Б, 2А … 11А, индивидуальное обучение — в конце своей параллели
export function compareClasses(a, b) {
  const pa = /^(\d+)\s*(.*)$/.exec(a) || [a, '99', a];
  const pb = /^(\d+)\s*(.*)$/.exec(b) || [b, '99', b];
  const diff = Number(pa[1]) - Number(pb[1]);
  if (diff) return diff;
  return pa[2].localeCompare(pb[2], 'ru');
}

// Кабинеты по номерам: 1.124 < 2.26 < 2.33 < … ; «Зал ФЗК» — после чисел
export const compareRooms = (a, b) => (a || '').localeCompare(b || '', 'ru', { numeric: true });

// Состояние дня в момент now:
//   off    — сегодня уроков нет (воскресенье, каникулы в расписании)
//   before — уроки ещё не начались, next — первые уроки
//   lesson — идёт урок, current — уроки сейчас, endsAt — конец урока
//   break  — перемена, next — уроки после неё, endsAt — их начало
//   over   — уроки на сегодня закончились
export function dayStatus(lessons, now = moscowNow()) {
  const today = lessons.filter((l) => l.day === now.day);
  if (today.length === 0) return { state: 'off' };

  const t = now.seconds;
  const current = today.filter((l) => toSeconds(l.start) <= t && t < toSeconds(l.end));
  if (current.length > 0) {
    // Разные классы заканчивают одновременно; берём ближайший конец
    const endsAt = Math.min(...current.map((l) => toSeconds(l.end)));
    const number = Math.min(...current.map((l) => l.number));
    return { state: 'lesson', current, endsAt, left: endsAt - t, number };
  }

  const upcoming = today.filter((l) => toSeconds(l.start) > t);
  if (upcoming.length === 0) return { state: 'over' };
  const startsAt = Math.min(...upcoming.map((l) => toSeconds(l.start)));
  const next = upcoming.filter((l) => toSeconds(l.start) === startsAt);
  const started = today.some((l) => toSeconds(l.start) <= t);
  return {
    state: started ? 'break' : 'before',
    next,
    endsAt: startsAt,
    left: startsAt - t,
    number: Math.min(...next.map((l) => l.number)),
  };
}

// 23 минуты (23:15); больше часа — 1 ч 05 мин (01:05:12)
export function formatCountdown(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) {
    return { words: `${h} ч ${pad(m)} мин`, clock: `${pad(h)}:${pad(m)}:${pad(sec)}` };
  }
  // Слова совпадают с часами: при 34:30 — «34 минуты», а не округлённые 35
  const words = m === 0 ? 'меньше минуты' : `${m} ${plural(m, 'минута', 'минуты', 'минут')}`;
  return { words, clock: `${pad(m)}:${pad(sec)}` };
}

// Строки блока «Сейчас»: по кабинетам; в одном кабинете может быть несколько классов
export function roomRows(lessons) {
  return [...lessons].sort(
    (a, b) => compareRooms(a.room, b.room) || compareClasses(a.class_name, b.class_name),
  );
}

// Сетка расписания для выбранного класса/учителя/кабинета:
// { days: [0..5 с уроками], numbers: [1..N], cells: {"day-number": [lessons]}, times: {number: "08:40–09:20"} }
export function buildGrid(lessons) {
  const cells = {};
  const times = {};
  const days = new Set();
  const numbers = new Set();
  lessons.forEach((l) => {
    const key = `${l.day}-${l.number}`;
    (cells[key] = cells[key] || []).push(l);
    days.add(l.day);
    numbers.add(l.number);
    if (!times[l.number]) times[l.number] = `${l.start}–${l.end}`;
  });
  return {
    days: [...days].sort((a, b) => a - b),
    numbers: [...numbers].sort((a, b) => a - b),
    cells,
    times,
  };
}
